/**
 * InventoryManager — Singleton that controls the InventoryPanel lifecycle.
 * Handles open/close/toggle, Playwright test hooks, and global `I` hotkey.
 *
 * @module InventoryManager
 */

import DebugLog from '../debug/DebugLog.js';
import EventBus from '../core/EventBus.js';
import ItemSystem from '../systems/ItemSystem.js';
import CharacterSystem from '../systems/CharacterSystem.js';

// ─── InventoryManager ─────────────────────────────────────────────────────────

const InventoryManager = {
  /** @type {import('./InventoryPanel.js').default|null} */
  _panel: null,

  /** @type {boolean} Whether the inventory panel is currently open */
  panelOpen: false,

  /**
   * Initialize the manager.
   * Lazy-imports InventoryPanel on first open to avoid circular deps at boot.
   * Wires EventBus inventory hotkey and registers window.__debug helpers.
   */
  init() {
    // Listen for the inventory hotkey fired by InputManager / scenes
    EventBus.on('input:inventory', ({ charId } = {}) => {
      this.toggle(charId);
    });

    this._registerDebugHooks();

    DebugLog.info('[ui] InventoryManager initialized');
  },

  // ─── Panel access ─────────────────────────────────────────────────────────

  /**
   * Lazily create the InventoryPanel the first time it is needed.
   * @returns {Promise<import('./InventoryPanel.js').default>}
   */
  async _getPanel() {
    if (this._panel) return this._panel;

    const { default: InventoryPanel } = await import('./InventoryPanel.js');
    this._panel = new InventoryPanel();
    return this._panel;
  },

  // ─── Public API ───────────────────────────────────────────────────────────

  /**
   * Open the inventory panel for a specific character.
   * Defaults to the first P1 character if no ID is supplied.
   * @param {string} [charId]
   */
  async open(charId) {
    const panel = await this._getPanel();
    panel.show(charId ?? null);
    this.panelOpen = true;
    DebugLog.info(`[ui] InventoryManager.open; charId=${charId ?? 'default'}`);
  },

  /**
   * Close the inventory panel.
   */
  async close() {
    if (!this._panel) return;
    this._panel.hide();
    this.panelOpen = false;
    DebugLog.info('[ui] InventoryManager.close');
  },

  /**
   * Toggle the inventory panel.
   * @param {string} [charId]
   */
  async toggle(charId) {
    if (this._panel?.isVisible()) {
      await this.close();
    } else {
      await this.open(charId);
    }
  },

  // ─── Debug hooks ──────────────────────────────────────────────────────────

  _registerDebugHooks() {
    if (typeof window === 'undefined') return;

    // Implement window.__debugGive for the DebugConsole `give` command
    window.__debugGive = (itemId, qty = 1, charNameOrId) => {
      ItemSystem.init();
      const allChars = CharacterSystem.getAll();

      let char = null;
      if (charNameOrId) {
        char = CharacterSystem.getById(charNameOrId)
          ?? allChars.find(c => c.name.toLowerCase() === String(charNameOrId).toLowerCase())
          ?? null;
      }
      if (!char) {
        // Use party state if available, else first hero
        const partyState = window.__partyState;
        if (partyState?.characters?.length > 0) {
          const first = partyState.characters[0];
          char = typeof first === 'string' ? CharacterSystem.getById(first) : first;
        }
        if (!char) {
          char = allChars.find(c => c.isHero) ?? allChars[0] ?? null;
        }
      }

      if (!char) return 'No character found to give item to.';

      const base = ItemSystem.getBaseItem(itemId);
      if (!base) return `Unknown item id: "${itemId}"`;

      char.inventory = char.inventory ?? [];

      for (let i = 0; i < qty; i++) {
        const instance = ItemSystem.createInstance(itemId, { identified: true });
        if (!instance) continue;

        if (base.stackable) {
          const existing = char.inventory.find(it => it.baseId === itemId);
          if (existing) {
            existing.quantity = (existing.quantity ?? 1) + 1;
            continue;
          }
        }
        char.inventory.push(instance);
      }

      DebugLog.event(`[debug] give; item=${itemId} qty=${qty} char=${char.name}`);

      // Refresh panel if open
      if (this._panel?.isVisible()) {
        this._panel._renderAll();
      }

      return `Gave ${qty}x ${base.name} to ${char.name}`;
    };

    // Implement window.__debugGetInventory
    window.__debugGetInventory = (nameOrId) => {
      const allChars = CharacterSystem.getAll();
      let char = null;
      if (nameOrId) {
        char = CharacterSystem.getById(nameOrId)
          ?? allChars.find(c => c.name.toLowerCase() === String(nameOrId).toLowerCase())
          ?? null;
      }
      if (!char) {
        const partyState = window.__partyState;
        if (partyState?.characters?.length > 0) {
          const first = partyState.characters[0];
          char = typeof first === 'string' ? CharacterSystem.getById(first) : first;
        }
        if (!char) char = allChars.find(c => c.isHero) ?? allChars[0] ?? null;
      }
      if (!char) return null;

      return {
        character: char.name,
        inventory: char.inventory ?? [],
        equipment: char.equipment ?? {},
      };
    };

    // Wire into window.__debug (DebugConsole sets up the object first)
    const wireHooks = () => {
      if (!window.__debug) return;

      window.__debug.openInventory = (charId) => {
        this.open(charId);
        return `Inventory opened${charId ? ` for ${charId}` : ''}`;
      };

      window.__debug.listInventory = (charId) => {
        return window.__debugGetInventory?.(charId) ?? 'Inventory unavailable';
      };

      window.__debug.giveItem = (itemId, qty = 1, charNameOrId) => {
        return window.__debugGive?.(itemId, qty, charNameOrId) ?? 'Not available';
      };

      // Helper to get current party characters
      const getPartyChars = () => {
        const allChars = CharacterSystem.getAll?.() ?? [];
        if (allChars.length > 0) return allChars;
        const partyState = window.__partyState;
        if (!partyState) return [];
        const ids = Array.isArray(partyState.characters) ? partyState.characters : [];
        return ids.map(id => (typeof id === 'string' ? CharacterSystem.getById?.(id) : id)).filter(Boolean);
      };

      // Vendor debug helpers — open vendor panel with a generated stock
      window.__debug.openVendor = () => {
        import('./VendorPanel.js').then(m => {
          const mgr = window.__inventoryManager;
          if (!mgr._vendorPanel) {
            mgr._vendorPanel = new m.default();
            document.getElementById('ui-overlay')?.appendChild(mgr._vendorPanel.el);
          }
          const chars = getPartyChars();
          const char = chars[0] ?? null;
          mgr._vendorPanel.show({ vendor: { id: 'debug_vendor', name: 'Debug Vendor', type: 'general', regionLevel: 1 }, character: char });
        });
        return 'Vendor panel opened';
      };

      window.__debug.closeInventory = () => {
        window.__inventoryManager?.close?.();
        return 'Inventory closed';
      };

      window.__debug.closeVendor = () => {
        window.__inventoryManager?._vendorPanel?.hide?.();
        return 'Vendor closed';
      };

      window.__debug.getPartyState = () => {
        const chars = getPartyChars();
        return chars.map(c => ({
          id: c.id, name: c.name, level: c.level,
          hp: c.hp, maxHP: c.maxHP, gold: null,
          equipped: c.equipment ?? {},
          inventoryCount: (c.inventory ?? []).length,
        }));
      };

      window.__debug.getCharacterState = (nameOrId) => {
        const chars = getPartyChars();
        const c = chars.find(x => x.id === nameOrId || x.name === nameOrId) ?? chars[0];
        if (!c) return null;
        return { id: c.id, name: c.name, level: c.level, hp: c.hp, maxHP: c.maxHP,
          attributes: c.attributes, equipment: c.equipment, inventory: c.inventory };
      };
    };

    // Try immediately, and also after a short delay for late init
    wireHooks();
    setTimeout(wireHooks, 500);
  },
};

export default InventoryManager;
